import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  SpaceBetween,
  Wizard,
  Container,
  Link,
  StatusIndicator,
  WizardProps,
  Box,
} from "@amzn/awsui-components-react-v3/polaris";
import { PageLayout } from "../components/page-layout";
import { useSetOTPPin } from "../hooks/pin";
import { useRegister } from "../hooks/webauthn";
import { RegisterMfaForm } from "../components/register-mfa-form";
import { PinForm } from "../components/pin-form";
import { useAuth } from "react-oidc-context";
import { useGetUserQuery } from "../services/idp/idp";
import { OTP_PIN_PRESET } from "../lib/pin-presets";
import { LogoutCountDown } from "../components/logout-countdown";
import { AlternativeDomainRegistrationAlert } from "../components/alternative-domain-registration-alert";
import { isOtpEnabledForAuthedUser } from "../utils/permissions";
import { SupportUrls, AppTitles } from "../lib/constants";
import { BlockStatusIndicator } from "../components/block-status-indicator";

interface RegisterKeyWizardProps {
  instructionsLink: string;
}

interface WizardStep extends WizardProps.Step {
  customPrimaryActions?: React.ReactNode;
}

const StepIds = {
  REGISTER_KEY: "registerKey",
  SET_OTP_PIN: "setOtpPin",
  OTP_WARNING: "otpWarning",
  COMPLETE: "complete",
} as const;

/*
    First step: nickname and register FIDO2 security key
    Conditional second step: if the FIDO2 security key is a known key, show the Set OTP PIN form even if the user is not currently enabled to use OTP
    Conditional second step: if the user is enabled to use OTP but did not register a known key, we show a warning page with an Acknowledge button
    Final step: Show a success message and begin the logout countdown, also display the domain migration alert if applicable

    Because steps will not be the same for every user, steps are only shown in the step navigation column as they
    are progressed through, and manual navigation between steps is disabled.
 */
export const RegisterKeyWizard = ({
  instructionsLink,
}: RegisterKeyWizardProps) => {
  const auth = useAuth();
  const { register, error, isSuccess, otpRegistered } = useRegister();
  const { setPin, pinError, isPinSuccess, isPinLoading } = useSetOTPPin();
  const [visitedSteps, setVisitedSteps] = useState<string[]>([
    StepIds.REGISTER_KEY,
  ]);

  const focusTrapRef = useRef<HTMLSpanElement>(null);

  const username = auth.user?.profile?.preferred_username as string | undefined;
  // load the user only on the OTP PIN step so we can check if they already have a PIN set
  const { data: userData } = useGetUserQuery(username || "", {
    skip: !visitedSteps.includes(StepIds.SET_OTP_PIN) || !username,
  });

  const otpPinAlreadySet = userData?.user?.otpPinSet ?? false;

  const navigateToStep = (stepId: string) => {
    setVisitedSteps((prev) => [...prev, stepId]);
    requestAnimationFrame(() =>
      // the double requestAnimationFrame ensures our focus call happens exactly one frame later than the main component
      // update, which ensures it runs after any internal focus calls from libraries such as Cloudscape; without the
      // double request, Cloudscape's own focus call (https://github.com/cloudscape-design/components/blob/2aad40e5f0fce7d6b8fef55be0d6586e8ec45beb/src/wizard/wizard-form.tsx#L55-L57)
      // would steal the focus when we auto-advance the step, resulting in a blue outline around the step title
      requestAnimationFrame(() => focusTrapRef.current?.focus()),
    );
  };

  const allSteps: Record<string, WizardStep> = {
    [StepIds.REGISTER_KEY]: {
      title: "Register a new security key",
      customPrimaryActions: <></>,
      content: (
        <RegisterMfaForm
          error={error}
          onSubmit={register}
          isSuccess={isSuccess}
          isChromium={window.chrome}
        />
      ),
    },
    [StepIds.SET_OTP_PIN]: {
      title: "Set your OTP PIN",
      isOptional: otpPinAlreadySet,
      customPrimaryActions: otpPinAlreadySet ? (
        <Button
          variant="normal"
          onClick={() => navigateToStep(StepIds.COMPLETE)}
        >
          Skip
        </Button>
      ) : (
        <></>
      ),
      content: (
        <SpaceBetween size="l">
          {otpPinAlreadySet && (
            <StatusIndicator type="info">
              You already have a OTP PIN set. If you wish to change it, you can
              do so now. Otherwise, press &ldquo;Skip&rdquo; to continue.
            </StatusIndicator>
          )}
          <PinForm
            error={pinError}
            onSubmit={setPin}
            isLoading={isPinLoading}
            {...OTP_PIN_PRESET}
          />
        </SpaceBetween>
      ),
    },
    [StepIds.OTP_WARNING]: {
      title: "OTP acknowledgment",
      customPrimaryActions: (
        <Button
          variant="primary"
          onClick={() => navigateToStep(StepIds.COMPLETE)}
        >
          Acknowledge
        </Button>
      ),
      content: (
        <SpaceBetween size="l">
          <BlockStatusIndicator type="warning">
            You are in a permissions group that allows you to authenticate to
            certain applications with OTP, but the key you just registered will
            not work for OTP in this environment.
          </BlockStatusIndicator>
          <Box>
            If you need access to the Leo handheld scanners or another
            application that requires OTP and you have not already registered an
            OTP-enabled YubiKey, please visit your nearest KITS office or{" "}
            <Link href={SupportUrls.YUBIKEY_REQUEST} external>
              cut a ticket
            </Link>
            to the Leo IAM team.
          </Box>
        </SpaceBetween>
      ),
    },
    [StepIds.COMPLETE]: {
      title: "Success!",
      customPrimaryActions: <></>,
      content: (
        <SpaceBetween size="l">
          <BlockStatusIndicator type="success">
            Registration is complete and you are ready to use your security key
            to log in to applications through Frontier Auth.
          </BlockStatusIndicator>
          <LogoutCountDown auth={auth} startingSeconds={30} />
          <AlternativeDomainRegistrationAlert alias={username} />
        </SpaceBetween>
      ),
    },
  };

  const steps = visitedSteps.map((id) => allSteps[id]);
  const activeStepIndex = steps.length - 1; // we don't allow self-navigation between steps, so we are always on the "last" step

  const otpEnabled = isOtpEnabledForAuthedUser(auth);

  useEffect(() => {
    if (isSuccess) {
      if (otpRegistered) {
        navigateToStep(StepIds.SET_OTP_PIN);
        return;
      }
      if (otpEnabled) {
        navigateToStep(StepIds.OTP_WARNING);
        return;
      }
      navigateToStep(StepIds.COMPLETE);
    }
  }, [isSuccess, otpRegistered, otpEnabled]);

  useEffect(() => {
    if (isPinSuccess) {
      navigateToStep(StepIds.COMPLETE);
    }
  }, [isPinSuccess]);

  return (
    <PageLayout
      auth={auth}
      disableNavigation
      maxContentWidth={1150}
      title={AppTitles.REGISTRATION_WIZARD}
    >
      {/* following Cloudscape best practices and not wrapping the registration wizard component in a ContentLayout
      unlike the other pages (ref: https://cloudscape.design/components/wizard/?tabId=usage) */}
      <SpaceBetween size="m">
        <Container>
          <Wizard
            i18nStrings={{
              stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
              collapsedStepsLabel: (stepNumber, stepsCount) =>
                `Step ${stepNumber}`,
              navigationAriaLabel: "Steps",
              optional: "optional",
            }}
            isLoadingNextStep // disables self-navigation between steps
            activeStepIndex={activeStepIndex}
            steps={steps}
            customPrimaryActions={steps[activeStepIndex]?.customPrimaryActions}
          />
        </Container>
        <Container>
          <StatusIndicator type="info" /> <b>Need help?</b> Click{" "}
          <Link external href={instructionsLink} variant="primary">
            here
          </Link>{" "}
          for registration instructions.
        </Container>
        <span ref={focusTrapRef} tabIndex={-1} />
      </SpaceBetween>
    </PageLayout>
  );
};
